import { create } from 'zustand'
import { supabase } from '../supabase'
import { registerPlugin, Capacitor } from '@capacitor/core';
import type { Session, User } from '@supabase/supabase-js'
import { App as CapApp } from '@capacitor/app'
import { Browser } from '@capacitor/browser'
import { getAppUrl } from '@/lib/utils/url'
import { queryClient } from '../query-client'

export interface ShuvoSharedStorePlugin {
    setSessionToken(options: { token: string, refreshToken: string, userId: string }): Promise<void>;
    getSessionToken(): Promise<{ token?: string, refreshToken?: string, userId?: string }>;
    clearSessionToken(): Promise<void>;
}

const ShuvoBridge = registerPlugin<ShuvoSharedStorePlugin>('ShuvoSharedStore');

interface AuthState {
    session: Session | null
    user: User | null
    loading: boolean
    initialized: boolean
    displayName: string
    avatarUrl: string
    avatarColorIndex: number
    isPro: boolean
    maxCollectionsOverride: number | null
    fetchProfile: () => Promise<void>
    updateProfile: (data: { displayName?: string; avatarUrl?: string; avatarColorIndex?: number }) => Promise<{ error: any }>
    signInWithOtp: (email: string) => Promise<{ error: any }>
    verifyOtp: (email: string, token: string, type?: 'signup' | 'magiclink' | 'recovery' | 'email') => Promise<{ error: any; session: Session | null }>
    signOut: () => Promise<{ error: any }>
    initialize: () => Promise<void>
    listenForDeepLink: () => void
    signInWithSocial: (provider: 'google' | 'apple') => Promise<{ error: any }>
    signInWithPassword: (email: string, password: string) => Promise<{ error: any }>
}

export const useAuthStore = create<AuthState>((set, get) => ({
    session: null,
    user: null,
    loading: false,
    initialized: false,
    displayName: "",
    avatarUrl: "",
    avatarColorIndex: 0,
    isPro: false,
    maxCollectionsOverride: null,

    fetchProfile: async () => {
        const { user } = get()
        if (!user) return

        // 1. Try to fetch existing profile
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', user.id)
            .single()

        if (error && error.code !== 'PGRST116') {
            console.error('Error fetching profile:', error)
            return
        }

        let profileData = data

        // 2. If no profile exists, create it
        if (!profileData) {
            const newProfile = {
                id: user.id,
                email: user.email,
                display_name: user.user_metadata?.display_name || user.email?.split('@')[0] || '',
                avatar_color_index: Math.floor(Math.random() * 10),
                updated_at: new Date().toISOString()
            }

            const { data: created, error: createError } = await supabase
                .from('profiles')
                .insert(newProfile)
                .select()
                .single()

            if (createError) {
                console.error('Error creating profile:', createError)
                return
            }
            profileData = created
        }

        // 3. Ensure color index is always set (for legacy profiles)
        if (profileData && (profileData.avatar_color_index === null || profileData.avatar_color_index === undefined)) {
            const colorIndex = Math.floor(Math.random() * 10)
            const { data: updated } = await supabase
                .from('profiles')
                .update({ avatar_color_index: colorIndex })
                .eq('id', user.id)
                .select()
                .single()
            if (updated) profileData = updated
        }

        if (profileData) {
            set({
                displayName: profileData.display_name || '',
                avatarUrl: profileData.avatar_url || '',
                avatarColorIndex: profileData.avatar_color_index ?? 0,
                isPro: profileData.is_pro ?? false,
                maxCollectionsOverride: profileData.max_collections_override ?? null
            })
        }
    },

    updateProfile: async (updates: { displayName?: string; avatarUrl?: string; avatarColorIndex?: number }) => {
        const user = get().user
        if (!user) return { error: new Error('Not authenticated') }

        const dbUpdates: any = {
            id: user.id,
            updated_at: new Date().toISOString()
        }
        if (updates.displayName !== undefined) dbUpdates.display_name = updates.displayName
        if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl
        if (updates.avatarColorIndex !== undefined) dbUpdates.avatar_color_index = updates.avatarColorIndex

        const { error } = await supabase
            .from('profiles')
            .upsert(dbUpdates)

        if (!error) {
            set((state) => ({
                displayName: updates.displayName ?? state.displayName,
                avatarUrl: updates.avatarUrl ?? state.avatarUrl,
                avatarColorIndex: updates.avatarColorIndex ?? state.avatarColorIndex
            }))
        }
        return { error }
    },

    initialize: async () => {
        if (get().initialized) return

        // Use onAuthStateChange as the PRIMARY driver.
        // It fires immediately with INITIAL_SESSION from the local cache — no network wait.
        // Token refresh (if needed) happens silently in the background.
        supabase.auth.onAuthStateChange(async (event, session) => {
            console.log(`🚨 [AUTH DIAGNOSTIC] onAuthStateChange fired. Event: ${event}`, { 
                hasSession: !!session, 
                userId: session?.user?.id 
            })
            const currentUser = get().user
            const newUser = session?.user ?? null

            // Set token in native storage for Share Extensions
            try {
                if (session?.access_token && session?.refresh_token && newUser?.id) {
                    if (Capacitor.getPlatform() === 'ios') {
                        ShuvoBridge.setSessionToken({ 
                            token: session.access_token, 
                            refreshToken: session.refresh_token,
                            userId: newUser.id 
                        }).catch(console.error)
                    } else if (Capacitor.getPlatform() === 'android') {
                        import("@capacitor/preferences").then(({ Preferences }) => {
                            Preferences.set({ key: 'supabase_session_token', value: session.access_token });
                            Preferences.set({ key: 'supabase_refresh_token', value: session.refresh_token });
                            Preferences.set({ key: 'supabase_user_id', value: newUser.id });
                        });
                    }
                } else if (!session?.access_token) {
                    if (Capacitor.getPlatform() === 'ios') {
                        ShuvoBridge.clearSessionToken().catch(console.error)
                    } else if (Capacitor.getPlatform() === 'android') {
                        import("@capacitor/preferences").then(({ Preferences }) => {
                            Preferences.remove({ key: 'supabase_session_token' });
                            Preferences.remove({ key: 'supabase_refresh_token' });
                            Preferences.remove({ key: 'supabase_user_id' });
                        });
                    }
                }
            } catch (syncError) {
                console.error("Failed to sync to native storage", syncError);
            }

            // Mark as initialized on the very first event (INITIAL_SESSION).
            // This fires instantly from local storage, not from the network.
            if (!get().initialized) {
                set({ session, user: newUser, initialized: true })
                if (newUser) get().fetchProfile()
                return
            }

            // If user signed out or changed, clear the pick store
            if (event === 'SIGNED_OUT' || !newUser || (currentUser && currentUser.id !== newUser.id)) {
                try {
                    const { usePickStore } = await import("./usePickStore")
                    usePickStore.getState().clearStore()
                } catch (err) {
                    console.error("Failed to clear store on auth change:", err)
                }
            }

            set({ session, user: newUser, loading: false })
            if (newUser) get().fetchProfile()
        })
    },

    signInWithOtp: async (email: string) => {
        set({ loading: true })
        // On native (Android/iOS), use the custom URL scheme so the Magic Link forcefuly opens the app.
        // On web, fall back to the normal browser origin.
        const redirectTo = Capacitor.isNativePlatform()
            ? 'shuvo://auth/callback'
            : getAppUrl()

        const { error } = await supabase.auth.signInWithOtp({
            email,
            options: {
                shouldCreateUser: true,
                emailRedirectTo: redirectTo,
            }
        })
        set({ loading: false })
        return { error }
    },

    verifyOtp: async (email: string, token: string, type: 'signup' | 'magiclink' | 'recovery' | 'email' = 'email') => {
        set({ loading: true })
        
        // 1. Try initial verification (usually 'email' for OTP)
        const { data, error } = await supabase.auth.verifyOtp({
            email,
            token,
            type,
        })

        // 2. Fallback: If 'email' fails, it might be a first-time signup which requires 'signup' type
        if (error && type === 'email') {
            const { data: retryData, error: retryError } = await supabase.auth.verifyOtp({
                email,
                token,
                type: 'signup'
            })
            if (!retryError && retryData.session) {
                set({ 
                    session: retryData.session, 
                    user: retryData.session.user,
                    loading: false 
                })
                return { error: null, session: retryData.session }
            }
        }

        if (data.session) {
            set({ session: data.session, user: data.session.user })
        }

        set({ loading: false })
        return { error, session: data.session }
    },

    signOut: async () => {
        console.log(`🚨 [AUTH DIAGNOSTIC] Manual signOut called! Trace:`, new Error().stack)
        set({ loading: true })
        // Clear pick store first to prevent flashing old data
        try {
            const { usePickStore } = await import("./usePickStore")
            usePickStore.getState().clearStore()
        } catch (err) {
            console.error("Failed to clear pick store on logout:", err)
        }

        // Clear React Query cache to prevent data leakage between users
        queryClient.clear()

        // Clear Capacitor Preferences for onboarding so new users on same device see it
        try {
            const { Preferences } = await import("@capacitor/preferences")
            await Preferences.remove({ key: 'has_onboarded' })
        } catch (err) {
            console.error("Failed to clear preferences on logout:", err)
        }

        const { error } = await supabase.auth.signOut({ scope: 'local' })
        set({ session: null, user: null, loading: false })
        return { error }
    },

    listenForDeepLink: () => {
        if (!Capacitor.isNativePlatform()) return

        // Register immediately to catch any incoming links while app is warming up
        CapApp.addListener('appUrlOpen', async ({ url }) => {
            // The magic link lands as: shuvo://login#access_token=...&refresh_token=...
            // Supabase needs the fragment part as a query string to parse it.
            const fragment = url.split('#')[1]
            if (!fragment) return

            // Convert the fragment into a proper URL that Supabase can parse
            const params = new URLSearchParams(fragment)
            const access_token = params.get('access_token')
            const refresh_token = params.get('refresh_token')

            if (access_token && refresh_token) {
                if (Capacitor.isNativePlatform()) {
                    Browser.close().catch(() => {})
                }

                const { data, error } = await supabase.auth.setSession({
                    access_token,
                    refresh_token,
                })

                if (!error && data.session) {
                    set({ session: data.session, user: data.session.user, loading: false })
                    // fetchProfile will be called by the onAuthStateChange listener
                } else {
                    set({ loading: false })
                }
            }
        })
    },
    
    signInWithSocial: async (provider: 'google' | 'apple') => {
        set({ loading: true })
        // Use Custom Scheme on Native to guarantee return, fallback to valid React route on Web
        const redirectTo = Capacitor.isNativePlatform()
            ? 'shuvo://auth/callback'
            : `${getAppUrl()}/auth/method`

        const { data, error } = await supabase.auth.signInWithOAuth({
            provider,
            options: {
                redirectTo,
                skipBrowserRedirect: Capacitor.isNativePlatform(),
                queryParams: provider === 'google' ? { prompt: 'select_account' } : undefined
            }
        })
        
        if (error) {
            set({ loading: false })
            return { error }
        }

        if (Capacitor.isNativePlatform() && data?.url) {
            await Browser.open({ 
                url: data.url,
                presentationStyle: 'popover' 
            })
        }
        
        return { error: null }
    },

    signInWithPassword: async (email: string, password: string) => {
        set({ loading: true })
        const { data, error } = await supabase.auth.signInWithPassword({
            email,
            password
        })
        if (data.session) {
            set({ session: data.session, user: data.session.user })
        }
        set({ loading: false })
        return { error }
    },
}))
